import React, { useEffect, useMemo, useRef } from 'react';
import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const normalizeText = (text) => {
    if (typeof text !== 'string') return '';
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const stripLower = (text) => normalizeText(text).toLowerCase();

const findOptionScore = (question, response) => {
    const options = question?.questionOptionScores || [];
    const opt = options.find((o) => {
        const sid = o.scaleOptionId || o.scaleOption?.id || o.id;
        return Number(sid) === Number(response?.scaleOptionId);
    });
    if (!opt) return null;
    const score = toNumberOrNull(opt?.score);
    if (score != null) return score;
    const label = stripLower(opt?.scaleOption?.label || opt?.label || '');
    if (!label) return null;
    const labelMap = new Map(
        options.map((o) => [
            stripLower(o?.scaleOption?.label || o?.label || ''),
            toNumberOrNull(o?.score),
        ])
    );
    return labelMap.get(label) ?? null;
};

const deriveScoreFromResponse = (response, question) => {
    const optionScore = findOptionScore(question, response);
    if (optionScore != null) return optionScore;
    const fallback = toNumberOrNull(response?.score ?? response?.numericValue);
    if (fallback != null) return fallback;
    const label = stripLower(response?.textValue || '');
    if (!label) return 0;
    if (label === 'si' || label === 'sí') return 2;
    if (label === 'mucho') return 2;
    if (label === 'poco') return 1;
    return 0;
};

const computeCategoryLevels = (register, scale) => {
    if (!register || !scale) return [];
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    const factors = Array.isArray(scale?.factors) ? scale.factors : [];
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    if (!questions.length || !responses.length) return [];

    const factorById = new Map(factors.map((factor) => [factor.id, factor]));
    const factorScores = new Map();
    const questionById = new Map(questions.map((q) => [q.id, q]));

    const getFactorEntry = (factorId) => {
        const key = factorId ?? 'none';
        if (!factorScores.has(key)) {
            const factor = factorById.get(factorId);
            factorScores.set(key, {
                id: factor?.id ?? key,
                label: factor?.name || factor?.code || `Categoria ${key}`,
                score: 0,
                maxScore: 0,
            });
        }
        return factorScores.get(key);
    };

    questions.forEach((question) => {
        const entry = getFactorEntry(question?.scaleFactorId);
        const maxQuestionScore = Math.max(
            ...(question.questionOptionScores || []).map((opt) => toNumberOrNull(opt?.score) ?? 0),
            0
        );
        entry.maxScore += maxQuestionScore;
    });

    responses.forEach((response) => {
        const question = questionById.get(response?.scaleQuestionId);
        if (!question) return;
        const entry = getFactorEntry(question?.scaleFactorId);
        const score = deriveScoreFromResponse(response, question);
        entry.score += score;
    });

    const categories = Array.from(factorScores.values())
        .filter((entry) => entry.maxScore > 0)
        .map((entry) => {
            const ratio = entry.maxScore ? entry.score / entry.maxScore : 0;
            let level = 'Bajo';
            if (ratio >= 0.66) level = 'Alto';
            else if (ratio >= 0.33) level = 'Medio';
            return {
                label: entry.label,
                level: `Nivel de interes ${level}`,
                ratio,
            };
        })
        .sort((a, b) => b.ratio - a.ratio);

    return categories;
};
export const ScaleResultLIK = ({
    title = 'Categorias ordenadas por nivel de interes:',
    categories = [],
    onComputedResult,
    register,
    scale,
}) => {
    const derivedCategories = useMemo(() => computeCategoryLevels(register, scale), [register, scale]);
    
    // Control para evitar bucles infinitos
    const lastReportedRef = useRef(null);

    useEffect(() => {
        if (derivedCategories && derivedCategories.length > 0 && typeof onComputedResult === 'function') {
            const resultString = JSON.stringify(derivedCategories);
            
            // Solo informamos si los datos han cambiado realmente
            if (lastReportedRef.current !== resultString) {
                lastReportedRef.current = resultString;
                
                // Usamos un pequeño delay para que el renderizado inicial no bloquee
                const timer = setTimeout(() => {
                    onComputedResult({ 
                        categories: derivedCategories,
                        totalScore: derivedCategories[0]?.level || 'N/A'
                    });
                }, 100);
                
                return () => clearTimeout(timer);
            }
        }
    }, [derivedCategories, onComputedResult]);

    const displayCategories = derivedCategories.length ? derivedCategories : categories;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 16, color: '#0d1b2a' }}>
                {title}
            </Typography>
            {/* Optimizamos el estilo de la lista para manejar muchos elementos */}
            <List style={{ paddingTop: 0, maxHeight: '60vh', overflow: 'auto' }}>
                {displayCategories.map((category, index) => {
                    const label = category?.label || category?.name || `Categoria ${index + 1}`;
                    const level = category?.level || category?.interest || 'Nivel de interes desconocido';
                    const formattedLabel = normalizeText(label).toUpperCase();
                    return (
                        <ListItem key={category?.id ?? index} disableGutters style={{ alignItems: 'flex-start', py: 0.5 }}>
                            <ListItemIcon style={{ minWidth: 24, marginTop: 4 }}>
                                <FiberManualRecordIcon style={{ fontSize: 12, color: '#0d1b2a' }} />
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{ style: { color: '#0d1b2a', fontSize: '0.9rem' } }}
                                primary={`${formattedLabel}: ${level}`}
                            />
                        </ListItem>
                    );
                })}
            </List>
            {!displayCategories.length && (
                <Typography variant="body2" style={{ color: '#5f6b7a' }}>
                    No hay categorias para mostrar.
                </Typography>
            )}
        </Paper>
    );
};

export default ScaleResultLIK;